(function () {
  const sectionSelector = '[data-gm3-class]';
  const mobileQuery = window.matchMedia('(max-width: 1024px)');
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const tracks = new Set();
  let frame = 0;
  let lastTime = 0;

  function toNumber(value, fallback) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getCssNumber(element, name, fallback) {
    return toNumber(window.getComputedStyle(element).getPropertyValue(name), fallback);
  }

  function setTrackMetrics(track) {
    const original = track.querySelector('[data-gm3-cm-loop-group="original"]');
    if (!original) return;

    const styles = window.getComputedStyle(track);
    const gap = toNumber(styles.columnGap || styles.gap, 0);
    const distance = original.getBoundingClientRect().width + gap;

    if (distance <= 0) return;

    track.gm3CmDistance = distance;
    const viewport = track.closest('[data-gm3-cm-viewport]');
    const viewportStyles = viewport ? window.getComputedStyle(viewport) : null;
    const viewportLeft = viewport ? viewport.getBoundingClientRect().left : 0;
    const viewportPadLeft = viewportStyles ? toNumber(viewportStyles.paddingLeft, 0) : 0;
    const currentOffset = Number.isFinite(track.gm3CmOffset) ? track.gm3CmOffset : 0;
    const targetLeft = viewportLeft + viewportPadLeft;
    track.gm3CmCenterOffset = currentOffset + targetLeft - original.getBoundingClientRect().left;
    track.gm3CmOffset = Number.isFinite(track.gm3CmOffset)
      ? normalizeOffset(track.gm3CmOffset, distance, track.gm3CmCenterOffset)
      : track.gm3CmCenterOffset;
    applyTrackOffset(track);
  }

  function normalizeOffset(offset, distance, centerOffset) {
    let normalized = offset;
    const minOffset = centerOffset - distance;
    const maxOffset = centerOffset + distance;

    while (normalized <= minOffset) {
      normalized += distance;
    }

    while (normalized > maxOffset) {
      normalized -= distance;
    }

    return normalized;
  }

  function applyTrackOffset(track) {
    const offset = Number.isFinite(track.gm3CmOffset) ? track.gm3CmOffset : 0;
    track.style.setProperty('--gm3-cm-offset', `${offset}px`);
  }

  function refreshTracks(section) {
    section.querySelectorAll('[data-gm3-cm-track]').forEach((track) => {
      tracks.add(track);
      setTrackMetrics(track);
    });
  }

  function setHoverState(track, isHovering) {
    track.gm3CmHovering = isHovering;
  }

  function setActiveTab(section, tabName) {
    section.querySelectorAll('[data-gm3-cm-tab]').forEach((tab) => {
      const isActive = tab.dataset.gm3CmTab === tabName;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    section.querySelectorAll('[data-gm3-cm-panel]').forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.gm3CmPanel === tabName);
    });

    section.querySelectorAll('[data-gm3-cm-action]').forEach((action) => {
      action.classList.toggle('is-hidden', action.dataset.gm3CmAction !== tabName);
    });

    refreshTracks(section);
  }

  function getTrackSpeed(track) {
    const section = track.closest(sectionSelector);
    if (!section) return 0;

    const baseSpeed = mobileQuery.matches
      ? getCssNumber(section, '--gm3-cm-speed-mobile', 14)
      : getCssNumber(section, '--gm3-cm-speed-pc', 18);

    return track.gm3CmHovering ? getCssNumber(section, '--gm3-cm-hover-speed', 11) : baseSpeed;
  }

  function tick(time) {
    if (!lastTime) {
      lastTime = time;
    }

    const deltaSeconds = Math.min((time - lastTime) / 1000, 0.05);
    lastTime = time;

    if (!reduceMotionQuery.matches) {
      tracks.forEach((track) => {
        if (!track.isConnected || !track.gm3CmDistance) {
          tracks.delete(track);
          return;
        }

        const direction = track.classList.contains('gm3-class__track--right') ? 1 : -1;
        const currentSpeed = Number.isFinite(track.gm3CmCurrentSpeed)
          ? track.gm3CmCurrentSpeed
          : getTrackSpeed(track);
        const targetSpeed = getTrackSpeed(track);
        const easedSpeed = currentSpeed + (targetSpeed - currentSpeed) * 0.14;

        track.gm3CmCurrentSpeed = easedSpeed;
        track.gm3CmOffset = normalizeOffset(
          (Number.isFinite(track.gm3CmOffset) ? track.gm3CmOffset : track.gm3CmCenterOffset) +
            direction * easedSpeed * deltaSeconds,
          track.gm3CmDistance,
          track.gm3CmCenterOffset
        );
        applyTrackOffset(track);
      });
    }

    frame = window.requestAnimationFrame(tick);
  }

  function ensureTicker() {
    if (!frame) {
      frame = window.requestAnimationFrame(tick);
    }
  }

  function initSection(section) {
    if (section.dataset.gm3CmInitialized === 'true') {
      refreshTracks(section);
      ensureTicker();
      return;
    }

    section.dataset.gm3CmInitialized = 'true';

    section.querySelectorAll('[data-gm3-cm-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        setActiveTab(section, tab.dataset.gm3CmTab);
      });
    });

    section.querySelectorAll('[data-gm3-cm-viewport]').forEach((viewport) => {
      const track = viewport.querySelector('[data-gm3-cm-track]');
      if (!track) return;

      viewport.addEventListener('mouseenter', () => setHoverState(track, true));
      viewport.addEventListener('mouseleave', () => setHoverState(track, false));
      viewport.addEventListener('focusin', () => setHoverState(track, true));
      viewport.addEventListener('focusout', () => setHoverState(track, false));
    });

    section.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => refreshTracks(section), { once: true });
      }
    });

    refreshTracks(section);
    ensureTicker();
  }

  function initAll() {
    document.querySelectorAll(sectionSelector).forEach(initSection);
  }

  let resizeFrame = 0;
  function handleResize() {
    window.cancelAnimationFrame(resizeFrame);
    resizeFrame = window.requestAnimationFrame(() => {
      document.querySelectorAll(sectionSelector).forEach(refreshTracks);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(initAll).catch(function () {});
  }

  window.addEventListener('load', initAll);
  window.addEventListener('resize', handleResize);

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.matches && event.target.matches(sectionSelector)) {
      initSection(event.target);
    }
  });
})();
